import { useEffect, useState } from "react";
import PageHeader from "../components/layout/PageHeader";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { Play, Square, Banknote, Clock, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { toast } from "sonner";

const fmt = (n) => (n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CashSessions() {
  const [active, setActive] = useState(undefined); // undefined=loading, null=none, obj=active
  const [history, setHistory] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [closeForm, setCloseForm] = useState(false);
  const [opening, setOpening] = useState("");
  const [closing, setClosing] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const [a, h] = await Promise.all([
      api.get("/cash-sessions/active"),
      api.get("/cash-sessions", { params: { limit: 50 } }),
    ]);
    setActive(a.data);
    setHistory(h.data);
  };

  useEffect(() => { load(); }, []);

  const openShift = async () => {
    setSaving(true);
    try {
      await api.post("/cash-sessions/open", { opening_amount: parseFloat(opening), notes });
      toast.success("Turno abierto");
      setOpenForm(false); setOpening(""); setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  const closeShift = async () => {
    if (!active) return;
    setSaving(true);
    try {
      await api.post(`/cash-sessions/${active.id}/close`, {
        closing_amount_declared: parseFloat(closing),
        notes,
      });
      toast.success("Turno cerrado");
      setCloseForm(false); setClosing(""); setNotes("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    setSaving(false);
  };

  return (
    <div className="flex-1 bg-slate-50 min-h-full">
      <PageHeader
        title="Turno de Caja"
        subtitle="Control de apertura, cierre y cuadre de caja"
        testId="cash-header"
        actions={
          active === null ? (
            <Button onClick={() => setOpenForm(true)} className="bg-green-600 hover:bg-green-700 rounded-sm font-bold" data-testid="cash-open-btn">
              <Play className="w-4 h-4 mr-2" /> Abrir turno
            </Button>
          ) : active ? (
            <Button onClick={() => { setClosing(""); setNotes(""); setCloseForm(true); }} className="bg-red-600 hover:bg-red-700 rounded-sm font-bold" data-testid="cash-close-btn">
              <Square className="w-4 h-4 mr-2" /> Cerrar turno
            </Button>
          ) : null
        }
      />

      <div className="p-6 sm:p-8 space-y-6">
        {/* Active session */}
        {active === undefined && <div className="text-slate-500 text-sm">Cargando…</div>}

        {active === null && (
          <div className="bg-white border border-dashed border-slate-300 rounded-sm p-12 text-center" data-testid="cash-no-active">
            <Clock className="w-10 h-10 text-slate-300 mx-auto" />
            <div className="mt-3 text-sm text-slate-600">
              No tienes un turno abierto. Abre un turno para comenzar a operar.
            </div>
          </div>
        )}

        {active && (
          <div className="bg-white border-2 border-green-500 rounded-sm overflow-hidden" data-testid="cash-active-card">
            <div className="bg-green-50 border-b-2 border-green-500 px-6 py-3 flex items-center gap-2">
              <span className="w-2 h-2 bg-green-600 rounded-full pulse-dot" />
              <span className="text-sm font-black uppercase tracking-wider text-green-800">Turno abierto</span>
              <span className="text-xs text-slate-600 ml-auto font-mono">
                desde {new Date(active.opened_at).toLocaleString("es-DO")}
              </span>
            </div>
            <div className="p-6 grid grid-cols-2 md:grid-cols-4 gap-4">
              <Stat label="Apertura" value={`RD$ ${fmt(active.opening_amount)}`} />
              <Stat label="Ventas" value={`RD$ ${fmt(active.sales_total)}`} accent="text-blue-700" />
              <Stat label="Premios pagados" value={`RD$ ${fmt(active.payouts_total)}`} accent="text-red-600" />
              <Stat label="Efectivo esperado" value={`RD$ ${fmt(active.expected_cash)}`} accent="text-green-700" big />
            </div>
            {active.cancellations_total > 0 && (
              <div className="px-6 pb-4 text-xs text-slate-600 font-mono">
                Cancelaciones en el turno: RD$ {fmt(active.cancellations_total)}
              </div>
            )}
          </div>
        )}

        {/* History */}
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-900">Historial de turnos</div>
            <span className="text-xs font-mono text-slate-500">{history.length} registros</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left">
                <Th>Cajero</Th><Th>Sucursal</Th><Th>Apertura</Th><Th>Cierre</Th><Th>Ventas</Th><Th>Pagos</Th><Th>Esperado</Th><Th>Declarado</Th><Th>Diferencia</Th><Th>Estado</Th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 ? (
                <tr><td colSpan="10" className="p-8 text-center text-slate-500">Sin turnos registrados</td></tr>
              ) : history.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cash-row-${s.id}`}>
                  <td className="p-3 font-semibold text-slate-900">{s.user_name}</td>
                  <td className="p-3 font-mono">{s.branch_code || "—"}</td>
                  <td className="p-3 font-mono text-xs">{new Date(s.opened_at).toLocaleString("es-DO")}</td>
                  <td className="p-3 font-mono text-xs">{s.closed_at ? new Date(s.closed_at).toLocaleString("es-DO") : "—"}</td>
                  <td className="p-3 font-mono text-right">RD${fmt(s.sales_total)}</td>
                  <td className="p-3 font-mono text-right">RD${fmt(s.payouts_total)}</td>
                  <td className="p-3 font-mono text-right">RD${fmt(s.expected_cash)}</td>
                  <td className="p-3 font-mono text-right">{s.closing_amount_declared != null ? `RD$${fmt(s.closing_amount_declared)}` : "—"}</td>
                  <td className="p-3 font-mono text-right font-bold">
                    {s.status === "closed" ? <DiffBadge diff={s.difference} /> : "—"}
                  </td>
                  <td className="p-3">
                    {s.status === "open" ? (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-green-100 text-green-700">Abierto</span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm bg-slate-100 text-slate-600">Cerrado</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Open dialog */}
      <Dialog open={openForm} onOpenChange={setOpenForm}>
        <DialogContent className="max-w-sm rounded-sm">
          <DialogHeader><DialogTitle>Abrir turno</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Efectivo inicial (RD$)</Label>
              <Input
                type="number" step="0.01" autoFocus
                value={opening} onChange={(e) => setOpening(e.target.value)}
                className="mt-1 rounded-sm font-mono text-xl font-bold h-12"
                data-testid="cash-open-amount"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider">Notas (opcional)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenForm(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={openShift} disabled={saving || !opening} className="bg-green-600 hover:bg-green-700 rounded-sm" data-testid="cash-open-save">
              {saving ? "Abriendo…" : "Abrir turno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Close dialog */}
      <Dialog open={closeForm} onOpenChange={setCloseForm}>
        <DialogContent className="max-w-md rounded-sm">
          <DialogHeader><DialogTitle>Cerrar turno</DialogTitle></DialogHeader>
          {active && (
            <div className="space-y-3">
              <div className="p-4 bg-slate-50 border border-slate-200 rounded-sm space-y-1 text-sm font-mono">
                <div className="flex justify-between"><span className="text-slate-600">Apertura</span><span>RD$ {fmt(active.opening_amount)}</span></div>
                <div className="flex justify-between"><span className="text-blue-700">+ Ventas</span><span className="text-blue-700">RD$ {fmt(active.sales_total)}</span></div>
                <div className="flex justify-between"><span className="text-red-600">− Premios</span><span className="text-red-600">RD$ {fmt(active.payouts_total)}</span></div>
                <div className="flex justify-between font-bold border-t border-slate-300 pt-1 mt-1"><span>= Esperado</span><span className="text-green-700">RD$ {fmt(active.expected_cash)}</span></div>
              </div>
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Efectivo declarado (RD$)</Label>
                <Input
                  type="number" step="0.01" autoFocus
                  value={closing} onChange={(e) => setClosing(e.target.value)}
                  className="mt-1 rounded-sm font-mono text-xl font-bold h-12"
                  data-testid="cash-close-amount"
                />
              </div>
              {closing && !isNaN(parseFloat(closing)) && (
                <div className="p-3 rounded-sm text-center font-mono border-2" style={{
                  borderColor: Math.abs(parseFloat(closing) - active.expected_cash) < 0.01 ? "#16a34a" : "#dc2626",
                  backgroundColor: Math.abs(parseFloat(closing) - active.expected_cash) < 0.01 ? "#f0fdf4" : "#fef2f2",
                }}>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-slate-600">Diferencia</div>
                  <div className={`text-2xl font-black ${parseFloat(closing) - active.expected_cash >= 0 ? "text-green-700" : "text-red-700"}`}>
                    {parseFloat(closing) - active.expected_cash >= 0 ? "+" : ""}RD$ {fmt(parseFloat(closing) - active.expected_cash)}
                  </div>
                </div>
              )}
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wider">Notas (opcional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1 rounded-sm" rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseForm(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={closeShift} disabled={saving || !closing} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="cash-close-save">
              {saving ? "Cerrando…" : "Cerrar turno"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, accent = "text-slate-950", big = false }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">{label}</div>
      <div className={`font-mono font-black ${big ? "text-3xl" : "text-2xl"} ${accent}`}>{value}</div>
    </div>
  );
}

function DiffBadge({ diff }) {
  if (Math.abs(diff) < 0.01) {
    return <span className="inline-flex items-center gap-1 text-green-700"><Minus className="w-3 h-3" />0.00</span>;
  }
  if (diff > 0) return <span className="inline-flex items-center gap-1 text-green-700"><TrendingUp className="w-3 h-3" />+{fmt(diff)}</span>;
  return <span className="inline-flex items-center gap-1 text-red-700"><TrendingDown className="w-3 h-3" />{fmt(diff)}</span>;
}

const Th = ({ children }) => <th className="p-3 text-xs uppercase tracking-wider font-bold text-slate-600">{children}</th>;
